import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  Paper,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Toaster, toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import * as services from "./../../services/services";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export const ROLE = {
  ADMIN: 1,
  DOCTOR: 2,
};

export default function EditUser() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newRePassword, setNewRePassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: "",
    newRePassword: "",
  });

  const [imagePreview, setImagePreview] = useState(null);

  const [form, setForm] = useState({
    username: "",
    password: "",
    rePassword: "",
    name: "",
    lastname: "",
    title: "",
    phoneNumber: "",
    idCard: "",
    license_no: "",
    image: "",
    hospitalId: "",
    roleId: ROLE.DOCTOR,
  });

  // 🔥 โหลดข้อมูล user
  const fetchUser = async () => {
    try {
      const res = await services.getUserById(id);
      const user = res.data.data;

      setForm({
        username: user.username || "",
        password: "",
        rePassword: "",
        name: user.name || "",
        lastname: user.lastname || "",
        title: user.title || "",
        phoneNumber: user.phoneNumber || "",
        idCard: user.idCard || "",
        license_no: user.license_no || "",
        image: user.image || "",
        hospitalId: user.hospitalId,
        roleId: user.roleId,
      });

      if (user.image) {
        setImagePreview(user.image);
      }
    } catch {
      toast.error("โหลดข้อมูล user ไม่สำเร็จ");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files?.[0]) {
      const file = files[0];
      setImagePreview(URL.createObjectURL(file));

      try {
        const res = await services.uploadImage(file);
        const imageUrl = res.data;

        setForm((prev) => ({
          ...prev,
          image: Array.isArray(imageUrl) ? imageUrl[0] : imageUrl,
        }));
      } catch {
        toast.error("Upload รูปไม่สำเร็จ");
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!form.username) {
      toast.error("กรุณากรอก อีเมล");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(form.username)) {
      toast.error("รูปแบบอีเมลไม่ถูกต้อง");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      const payload = { ...form };

      // 🔥 ถ้าไม่กรอก password → ไม่ส่งไป
      if (!payload.password) {
        delete payload.password;
        delete payload.rePassword;
      }

      const res = await services.updateUser({ id }, payload);

      if (!res.data.success) throw new Error();

      toast.success("อัปเดตสำเร็จ");

      setTimeout(() => {
        navigate("/users", { state: { refresh: true } });
      }, 800);
    } catch {
      toast.error("อัปเดตไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  // Password dialog handlers
  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setNewPassword("");
    setNewRePassword("");
    setPasswordErrors({ newPassword: "", newRePassword: "" });
  };

  const handleSaveNewPassword = async () => {
    if (!newPassword) {
      toast.error("กรุณากรอกรหัสผ่าน");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
      return;
    }

    if (newPassword !== newRePassword) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return;
    }

    if (!window.confirm("คุณแน่ใจจะเปลี่ยนรหัสผ่านของผู้ใช้นี้หรือไม่?"))
      return;

    try {
      setLoading(true);
      const payload = { ...form, password: newPassword };
      const res = await services.updateUser({ id }, payload);
      if (!res.data.success) throw new Error();
      toast.success("เปลี่ยนรหัสผ่านสำเร็จ");
      handleClosePasswordDialog();
    } catch {
      toast.error("เปลี่ยนรหัสผ่านไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <Typography p={3}>กำลังโหลด...</Typography>;
  }

  return (
    <>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" />

      <Box sx={{ maxWidth: 1000, mx: "auto", mt: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={600} mb={3}>
            แก้ไขบุคลากร
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
              gap: 4,
            }}
          >
            {/* รูป */}
            <Box
              sx={{
                border: "2px dashed #ddd",
                borderRadius: 3,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                minHeight: 250,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fafafa",
              }}
              onClick={() => document.getElementById("upload-input")?.click()}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="preview"
                  style={{
                    width: 160,
                    height: 160,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  คลิกเพื่ออัปโหลดรูป
                </Typography>
              )}

              <input
                id="upload-input"
                type="file"
                hidden
                accept="image/*"
                name="image"
                onChange={handleChange}
              />
            </Box>

            {/* form */}
            <Box>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* PASSWORD (use dialog) */}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setNewPassword("");
                        setNewRePassword("");
                        setPasswordErrors({
                          newPassword: "",
                          newRePassword: "",
                        });
                        setOpenPasswordDialog(true);
                      }}
                      fullWidth
                    >
                      เปลี่ยนรหัสผ่าน
                    </Button>
                  </Grid>

                  {/* NAME */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="คำนำหน้า"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="ชื่อ"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="นามสกุล"
                      name="lastname"
                      value={form.lastname}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* CONTACT */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="เบอร์โทร"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="เลขบัตรประชาชน"
                      name="idCard"
                      value={form.idCard}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="เลขที่ใบอนุญาต"
                      name="license_no"
                      value={form.license_no}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* ACTION */}
                  <Grid item xs={12}>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate("/users")}
                        disabled={loading}
                      >
                        ยกเลิก
                      </Button>

                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                      >
                        {loading ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Box>
        </Paper>
      </Box>
      {/* PASSWORD CHANGE DIALOG */}
      <Dialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>เปลี่ยนรหัสผ่าน</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="รหัสผ่านใหม่"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => {
              const v = e.target.value;
              setNewPassword(v);

              if (v.length > 0 && v.length < 8) {
                setPasswordErrors((p) => ({
                  ...p,
                  newPassword: "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
                }));
              } else {
                setPasswordErrors((p) => ({
                  ...p,
                  newPassword: "",
                }));
              }

              if (newRePassword.length > 0 && v !== newRePassword) {
                setPasswordErrors((p) => ({
                  ...p,
                  newRePassword: "รหัสผ่านไม่ตรงกัน",
                }));
              } else {
                setPasswordErrors((p) => ({
                  ...p,
                  newRePassword: "",
                }));
              }
            }}
            fullWidth
            margin="normal"
            error={newPassword.length > 0 && newPassword.length < 8}
            helperText={
              newPassword.length > 0 && newPassword.length < 8
                ? "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร"
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="ยืนยันรหัสผ่าน"
            type={showRePassword ? "text" : "password"}
            value={newRePassword}
            onChange={(e) => {
              const v = e.target.value;
              setNewRePassword(v);

              if (v.length > 0 && v !== newPassword) {
                setPasswordErrors((p) => ({
                  ...p,
                  newRePassword: "รหัสผ่านไม่ตรงกัน",
                }));
              } else {
                setPasswordErrors((p) => ({
                  ...p,
                  newRePassword: "",
                }));
              }
            }}
            fullWidth
            margin="normal"
            error={newRePassword.length > 0 && newRePassword !== newPassword}
            helperText={
              newRePassword.length > 0 && newRePassword !== newPassword
                ? "รหัสผ่านไม่ตรงกัน"
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowRePassword(!showRePassword)}
                  >
                    {showRePassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={loading}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSaveNewPassword}
            variant="contained"
            disabled={loading}
          >
            {loading ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
